import { abiaAcademyCheckService } from "api";
import { Loader } from "components";
import { StartAssessmentUI } from "features";
import { useApiRequest, useToast } from "hooks";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AssessmentProgress } from "..";
import { AbiaEssentialInfoUI } from "features/assessments/viewAssessment/abiaEssentialInfomation";

interface StartAssessmentProps {
  show: boolean;
  close: () => void;
  id: string;
  resume: (data: AssessmentProgress) => void;
  callback: (metric: string) => void;
}

const StartAssessment: React.FC<StartAssessmentProps> = ({
  show,
  close,
  id,
  resume,
  callback: nextStep,
}) => {
  const [clear, setClear] = useState(false);
  const navigate = useNavigate();

  // Hooks
  const { openToast } = useToast();

  // API Request Hooks
  const { run, data: response, requestStatus, error } = useApiRequest({});

  const handleAbiaCheck = (email) => {
    run(abiaAcademyCheckService({ email, assessment: id }));
  };

  const [callback, setCallback] = useState<{ func: () => void }>({
    func: () => {},
  });

  const [showDetails, setShowDetails] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dob: "",
    gender: "",
    phone: "",
  });

  useMemo(() => {
    if (response?.status === 201) {
      const res = response.data.data;
      setClear((prev) => !prev);

      localStorage.removeItem("isComplete");
      localStorage.setItem("candidate", JSON.stringify(res.candidate));
      const data = res.response_data;

      if (!data) {
        localStorage.setItem("answered", "1");
        setShowDetails(true);
        setCandidateDetails({
          firstName: res.verified_data["First Name"],
          lastName: res.verified_data["Last Name"],
          email: res.verified_data["Email"],
          dob: res.verified_data["Date of Birth"],
          gender: res.verified_data["Gender"],
          phone:
            extractAndFormatPhoneNumber(res.verified_data["Phone number"]) ??
            "",
        });
      } else {
        if (data.status === "completed") {
          openToast({
            show: true,
            heading: "Oops!",
            text: "It looks like you have already completed this assessment, the assessment can't be retaken.",
            type: "success",
            timeOut: 5000,
          });
        } else {
          setShowDetails(true);
          localStorage.setItem("candidateAssessment", JSON.stringify(data));
          localStorage.setItem("answered", `${data.questions_answered}`); // set number of answered questions
          localStorage.setItem(
            "secondsLeft",
            `${data.duration_minutes_left * 60}`
          );
          localStorage.setItem("assessmentId", `${data.id}`);
          setCandidateDetails({
            firstName: res.verified_data["First Name"],
            lastName: res.verified_data["Last Name"],
            email: res.verified_data["Email"],
            dob: res.verified_data["Date of Birth"],
            gender: res.verified_data["Gender"],
            phone:
              extractAndFormatPhoneNumber(res.verified_data["Phone number"]) ??
              "",
          });
          setCallback({
            func: () =>
              resume({
                basic_expectations: true,
                work_motivators: data.progress.work_motivators ?? false,
                work_qualities: data.progress.work_qualities ?? false,
                work_style: data.progress.work_style ?? false,
                personality: data.progress.personality ?? false,
                soft_skill: data.progress.soft_skill ?? false,
                domain_knowledge: data.progress.domain_knowledge ?? false,
              }),
          });
        }
      }

      close();
    } else if (error) {
      openToast({
        show: true,
        heading: "Sorry",
        text:
          error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          "Failed to initiate assessment, please try again or contact support",
        type: "error",
        timeOut: 5000,
      });
    }
  }, [response, error]);

  return (
    <>
      <Loader loading={requestStatus.isPending} />
      <StartAssessmentUI
        // submit={handleStart}
        submit={handleAbiaCheck}
        show={show}
        close={close}
        clear={clear}
      />
      <AbiaEssentialInfoUI
        show={showDetails}
        close={() => setShowDetails(false)}
        handleContinue={(metric: string) => {
          callback.func();
          setShowDetails(false);
          nextStep(metric);
        }}
        candidate={candidateDetails}
        resume={resume}
      />
    </>
  );
};

export { StartAssessment };

function extractAndFormatPhoneNumber(input) {
  let phone: string | null = null;

  // 1️⃣ Try to match tel: or number inside <a> tags
  const match = input.match(/tel:(\d+)|>(\d+)</i);
  if (match) {
    phone = match[1] || match[2];
  }

  // 2️⃣ If no match, try to find a plain number in the string
  if (!phone) {
    const plainMatch = input.match(/\d{7,}/); // Match at least 7 digits in a row
    phone = plainMatch ? plainMatch[0] : null;
  }

  if (!phone) return null;

  // 3️⃣ Clean and normalize
  phone = phone.replace(/\D/g, ""); // Remove non-digits

  // Ensure local Nigerian format starts with 0
  if (!phone.startsWith("0")) {
    phone = "0" + phone;
  }

  // Pad if less than 11 digits
  if (phone.length < 11) {
    phone = phone.padEnd(11, "0");
  }

  // Trim if more than 11 digits
  if (phone.length > 11) {
    phone = phone.slice(0, 11);
  }

  // Convert to +234 format
  return "+234" + phone.slice(1);
}
